import React from "react"
import { useId } from "@reach/auto-id"
import { useTranslation } from "react-i18next"

import { isEmailValid, isNotEmpty } from "utils/validation"
import { handleInternalInputValidation, handlePreventTabsValueAtEndOnInput } from "../formUtils"
import { Errors } from "../Errors"

export const InputMail = ({
    // Attributes
    id,
    value = "",
    name,
    label = "",
    placeholder = "",
    disabled,
    readonly = false,
    // API
    mailToValidate = false,
    initPassword = false,
    apiCallback,
    // Styles
    style,
    className = "",
    classNameLabel = "",
    classNameInput = "",
    // Error management
    mailToConfirm = null,
    required = false,
    customRules,
    showErrors,
    // Handler
    onChange,
    onValidate,
    autocomplete
}) => {
    const [errors, setErrors] = React.useState([])
    const [showpreventPasteError, setShowPreventPasteError] = React.useState(false)

    const { t } = useTranslation()

    const prefix = useId()
    const inputId = id ?? `${prefix}-${name}`
    const errorId = `${inputId}-error`

    const rules = React.useMemo(() => [
        {
            key: "mail-empty",
            check: (value) => required ? isNotEmpty(value) : true,
            message: t("mail_error_mandatory", { ns: "form" })
        },
        {
            key: "mail-invalid",
            check: (value) => isNotEmpty(value) ? isEmailValid(value) : true,
            message: t("mail_error_invalid", { ns: "form" })
        },
        {
            key: "mail-not-confirmed",
            check: (value) => (isNotEmpty(value) && mailToConfirm) ? (mailToConfirm === value) : true,
            message: t("mail_error_confirmation", { ns: "form" })
        },
        ...(customRules || [])
    ], [required, mailToConfirm, t]) // eslint-disable-line
    // customRules in dependencies array trigger an infinite loop,
    // because custom rules include a check property defining a new function,
    // preventing useMemo to return a valid shallow-comparison
    // or event useDeepMemo to return a valid deep-comparison

    const validate = React.useCallback(
        (currentValue) =>
            handleInternalInputValidation(currentValue, name, rules, setErrors, onValidate),
        [rules, name, onValidate]
    )

    // validate on mount and each time value change
    React.useEffect(() => {
        validate(value)
        return () => {
            // On unmounted erros and validation are no longer needed
            setErrors([])
            if (onValidate) onValidate(name, [])
        }
    }, [value, validate, onValidate, name])

    return (
        <>
            <div className={"c-form-group " + className} style={style}>
                <label className={"c-label mb-1 " + classNameLabel} htmlFor={inputId}>{label} {required && "*"}</label>
                <input
                    className={
                        "c-input "
                        + (showErrors && errors.length > 0 ? "has-error " : " ")
                        + (classNameInput ? classNameInput : "")
                    }
                    id={inputId}
                    name={name}
                    type={"text"}
                    placeholder={placeholder}
                    value={value}
                    disabled={disabled}
                    readOnly={readonly}
                    autoComplete={autocomplete ?? (mailToConfirm !== null) ? "off" : "email"}
                    required={required}
                    onChange={(e) => !readonly && onChange(handlePreventTabsValueAtEndOnInput(e))}
                    onBlur={() => validate(value)}
                    onPaste={(e) => {
                        if (mailToConfirm !== null) {
                            e.preventDefault()
                            setShowPreventPasteError(true)
                            setTimeout(() => setShowPreventPasteError(false), 2000)
                        }
                    }}
                    aria-describedby={errorId}
                    aria-invalid={Boolean(errors.length)}
                />

                {/* Error messages */}
                {showErrors && <Errors id={errorId} errors={errors} />}

                {showpreventPasteError &&
                    <p className="text-xs text-danger">{t("prevent_pasting", { ns: "form" })}</p>
                }
            </div>
        </>
    )
}
